/**
 * Absolute Elements Layer Component
 * Renders absolutely positioned elements sorted by z-index
 */

import type { AbsoluteElement } from '../../types/template.types';
import { AbsoluteImageElement } from './absolute-image-element';

interface AbsoluteElementsLayerProps {
  elements: AbsoluteElement[];
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
  onElementsChange?: (elements: AbsoluteElement[]) => void;
  onCutElement?: (elementId: string) => void;
  onCopyElement?: (elementId: string) => void;
  onPasteElement?: () => void;
  onDuplicateElement?: (elementId: string) => void;
  onBringToFront?: (elementId: string) => void;
  onSendToBack?: (elementId: string) => void;
  onBringForward?: (elementId: string) => void;
  onSendBackward?: (elementId: string) => void;
  hasClipboard?: boolean;
  gridSize?: number;
}

export function AbsoluteElementsLayer({
  elements,
  selectedElementId,
  onSelectElement,
  onElementsChange,
  onCutElement,
  onCopyElement,
  onPasteElement,
  onDuplicateElement,
  onBringToFront,
  onSendToBack,
  onBringForward,
  onSendBackward,
  hasClipboard = false,
  gridSize,
}: AbsoluteElementsLayerProps) {
  return (
    <>
      {[...elements]
        .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
        .map((element) => (
          <AbsoluteImageElement
            key={element.id}
            element={element}
            isSelected={element.id === selectedElementId}
            onSelect={() => onSelectElement(element.id)}
            onChange={(updated) => {
              if (onElementsChange) {
                const newElements = elements.map(el => 
                  el.id === updated.id ? updated : el
                );
                onElementsChange(newElements);
              }
            }}
            onDelete={() => {
              if (onElementsChange) {
                onElementsChange(elements.filter(el => el.id !== element.id));
              }
              onSelectElement(null);
            }}
            onCut={() => onCutElement?.(element.id)}
            onCopy={() => onCopyElement?.(element.id)}
            onPaste={() => onPasteElement?.()}
            onDuplicate={() => onDuplicateElement?.(element.id)}
            onBringToFront={() => onBringToFront?.(element.id)}
            onSendToBack={() => onSendToBack?.(element.id)}
            onBringForward={() => onBringForward?.(element.id)}
            onSendBackward={() => onSendBackward?.(element.id)}
            canPaste={hasClipboard}
            gridSize={gridSize}
          />
        ))}
    </>
  );
}
